import React, { useState, useMemo, useEffect } from "react";
import { X, Search, Check, Loader2, Image as ImageIcon, MessageSquare, LayoutGrid } from "lucide-react";
import { RcsComponent } from "../../../marketing/templates/rcs/rcs-types";
import { RcsPreview } from "../../../marketing/templates/rcs/rcs-preview";
import useCrmApi from "../../../hook/crm-req";

// Helper to get media URL (API uses media_url, types use mediaUrl)
const getMediaUrl = (comp: any): string | null => {
  return comp?.mediaUrl || comp?.media_url || null;
};

// Helper to get text content (API uses text, types use content)
const getTextContent = (comp: any): string | null => {
  return comp?.content || comp?.text || null;
};

// Helper to extract thumbnail/preview info from template components
const getTemplatePreviewInfo = (components: RcsComponent[]): {
  imageUrl: string | null;
  text: string | null;
  type: 'text' | 'image' | 'rich_card' | 'carousel' | 'other';
} => {
  for (const comp of components) {
    const c = comp as any;
    if (comp.type === "rich_card") {
      return {
        imageUrl: getMediaUrl(c),
        text: c.title || c.description || null,
        type: 'rich_card'
      };
    }
    if (comp.type === "image") {
      const url = getMediaUrl(c);
      if (url) return { imageUrl: url, text: null, type: 'image' };
    }
    if (comp.type === "carousel" && c.cards?.length) {
      const firstCard = c.cards[0];
      return {
        imageUrl: getMediaUrl(firstCard),
        text: firstCard.title || null,
        type: 'carousel'
      };
    }
  }
  // Check for text component
  const textComp = components.find(c => c.type === "text" && getTextContent(c));
  if (textComp) {
    return { imageUrl: null, text: getTextContent(textComp), type: 'text' };
  }
  return { imageUrl: null, text: null, type: 'other' };
};

// RCS Template from API
export interface RcsTemplate {
  id: string;
  template_name: string;
  channel: string;
  category: string;
  language: string;
  status?: string;
  components: RcsComponent[];
  variables?: Array<{ index: number; name?: string; example?: string }>;
  content?: string;
  // Twilio Content SID for send
  twilio_content_sid?: string;
}

interface RcsTemplatePickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (template: RcsTemplate) => void;
}

export const RcsTemplatePickerModal: React.FC<RcsTemplatePickerModalProps> = ({
  isOpen,
  onClose,
  onSelect,
}) => {
  const token = localStorage.getItem("authToken");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [previewTemplate, setPreviewTemplate] = useState<RcsTemplate | null>(null);
  const [templates, setTemplates] = useState<RcsTemplate[]>([]);
  const [loading, setLoading] = useState(true);

  const { makeRequest: fetchRcsTemplates } = useCrmApi(
    `/messages/templates`,
    "GET",
    { Authorization: `Bearer ${token}` }
  );

  // Fetch RCS templates when modal opens
  useEffect(() => {
    if (!isOpen) return;

    const loadTemplates = async () => {
      setLoading(true);
      try {
        const [response] = await fetchRcsTemplates(undefined, { channel: "RCS", limit: 50 });
        const data = Array.isArray(response) ? response : response?.data || [];
        setTemplates(data);
        // Set first template as preview by default
        if (data.length > 0) {
          setPreviewTemplate(data[0]);
        }
      } catch (error) {
        console.error("Failed to fetch RCS templates:", error);
        setTemplates([]);
      } finally {
        setLoading(false);
      }
    };

    loadTemplates();
  }, [isOpen]);

  // Reset preview when modal closes
  useEffect(() => {
    if (!isOpen) {
      setPreviewTemplate(null);
      setSelectedId(null);
    }
  }, [isOpen]);

  const filteredTemplates = useMemo(() => {
    return templates.filter((template) => {
      const name = template.template_name || "";
      const category = template.category || "";
      const matchesSearch =
        name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        category.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesSearch;
    });
  }, [templates, searchTerm]);

  // Click on template item to preview it
  const handlePreview = (template: RcsTemplate) => {
    setPreviewTemplate(template);
  };

  // Click "Use This Template" button to select and close
  const handleSelect = (template: RcsTemplate) => {
    setSelectedId(template.id);

    // Brief visual feedback before closing
    setTimeout(() => {
      onSelect(template);
      onClose();
      setSelectedId(null);
    }, 180);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4">
      <div className="bg-white dark:bg-gray-900 rounded-2xl w-full max-w-5xl max-h-[92vh] flex flex-col overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="px-6 py-5 border-b dark:border-gray-800 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-slate-900 dark:text-gray-100">RCS Templates</h2>
            <p className="text-slate-500 dark:text-gray-400 text-sm mt-0.5">Select a rich template to send</p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 dark:text-gray-500 hover:text-slate-600 dark:hover:text-gray-300 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Search Bar */}
        <div className="px-6 py-4 border-b dark:border-gray-800 bg-slate-50 dark:bg-gray-800/60">
          <div className="relative max-w-md">
            <Search className="absolute left-3.5 top-3 text-slate-400 dark:text-gray-500" size={18} />
            <input
              type="text"
              placeholder="Search templates..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 dark:text-gray-100 rounded-xl focus:outline-none focus:border-indigo-500 text-sm"
            />
          </div>
        </div>

        {/* Content - Split View */}
        <div className="flex-1 flex overflow-hidden">
          {/* Template List - Left Side */}
          <div className="w-1/2 border-r dark:border-gray-800 overflow-y-auto p-4">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="animate-spin text-indigo-500" size={32} />
              </div>
            ) : filteredTemplates.length > 0 ? (
              <div className="flex flex-col gap-2">
                {filteredTemplates.map((template) => {
                  const isSelected = selectedId === template.id;
                  const isPreviewing = previewTemplate?.id === template.id;
                  const previewInfo = getTemplatePreviewInfo(template.components || []);

                  return (
                    <div
                      key={template.id}
                      className={`relative border-2 rounded-xl p-3 cursor-pointer transition-all ${
                        isSelected
                          ? "border-indigo-600 dark:border-indigo-400 bg-indigo-50 dark:bg-indigo-500/10"
                          : isPreviewing
                          ? "border-purple-400 dark:border-purple-500 bg-purple-50 dark:bg-purple-500/10"
                          : "border-slate-200 dark:border-gray-700 hover:border-indigo-300 dark:hover:border-indigo-500/50 hover:bg-slate-50 dark:hover:bg-gray-800"
                      }`}
                      onClick={() => handlePreview(template)}
                    >
                      <div className="flex items-start gap-3">
                        {/* Thumbnail */}
                        <div className="flex-shrink-0">
                          {previewInfo.imageUrl ? (
                            <img
                              src={previewInfo.imageUrl}
                              alt=""
                              className="w-12 h-12 rounded-lg object-cover"
                            />
                          ) : (
                            <div className="w-12 h-12 rounded-lg bg-slate-100 dark:bg-gray-700 flex items-center justify-center">
                              {previewInfo.type === 'text' && <MessageSquare size={20} className="text-slate-400 dark:text-gray-500" />}
                              {previewInfo.type === 'carousel' && <LayoutGrid size={20} className="text-slate-400 dark:text-gray-500" />}
                              {(previewInfo.type === 'image' || previewInfo.type === 'rich_card') && <ImageIcon size={20} className="text-slate-400 dark:text-gray-500" />}
                              {previewInfo.type === 'other' && <MessageSquare size={20} className="text-slate-400 dark:text-gray-500" />}
                            </div>
                          )}
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-slate-800 dark:text-gray-100 truncate text-sm">
                            {template.template_name}
                          </h3>
                          <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                            <span className="text-[10px] px-1.5 py-0.5 bg-purple-100 dark:bg-purple-500/15 text-purple-700 dark:text-purple-400 rounded-full">
                              RCS
                            </span>
                            <span className="text-[10px] px-1.5 py-0.5 bg-slate-100 dark:bg-gray-700 text-slate-600 dark:text-gray-300 rounded-full">
                              {template.category}
                            </span>
                            {template.status && (
                              <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                                template.status === "APPROVED"
                                  ? "bg-green-100 dark:bg-green-500/15 text-green-700 dark:text-green-400"
                                  : "bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-400"
                              }`}>
                                {template.status}
                              </span>
                            )}
                          </div>
                          {/* Show text preview */}
                          {(previewInfo.text || template.content) && (
                            <p className="text-[11px] text-slate-500 dark:text-gray-400 mt-1 line-clamp-1">
                              {previewInfo.text || template.content}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Selected Checkmark */}
                      {isSelected && (
                        <div className="absolute top-2 right-2 bg-indigo-600 text-white rounded-full p-1">
                          <Check size={12} strokeWidth={3} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="text-5xl mb-4">📭</div>
                <p className="text-slate-400 dark:text-gray-500">No RCS templates found</p>
                <p className="text-slate-500 dark:text-gray-400 text-sm mt-1">
                  {searchTerm ? "Try a different search term" : "Create templates in Connect first"}
                </p>
              </div>
            )}
          </div>

          {/* Preview Panel - Right Side */}
          <div className="w-1/2 bg-slate-50 dark:bg-gray-800/60 p-4 overflow-y-auto flex flex-col items-center justify-start">
            {previewTemplate ? (
              <div className="w-full max-w-[300px]">
                <div className="mb-3 text-center">
                  <h3 className="font-semibold text-slate-800 dark:text-gray-100 text-sm">
                    {previewTemplate.template_name}
                  </h3>
                  <p className="text-xs text-slate-500 dark:text-gray-400 mt-0.5">
                    {previewTemplate.category} • {previewTemplate.language || "en"}
                  </p>
                </div>
                <RcsPreview
                  components={previewTemplate.components || []}
                  senderName="Preview"
                />
                <button
                  onClick={() => handleSelect(previewTemplate)}
                  className="w-full mt-4 bg-indigo-600 text-white rounded-xl py-2.5 font-semibold text-sm hover:bg-indigo-700 transition-colors"
                >
                  Use This Template
                </button>
              </div>
            ) : (
              <div className="text-center text-slate-400 dark:text-gray-500 py-20">
                <MessageSquare size={48} className="mx-auto mb-3 opacity-50" />
                <p className="text-sm">Hover over a template to preview</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
